/**
 * ITEM 32 — create/edit a caller-ID pool number.
 *
 * Layout mirrors `SipTrunkDrawer`: `ResourceDrawer` at `width="xl"` hosting a
 * `ZodForm` split across two tabs (see `DrawerFormKit`).
 *
 *   1. Basic Information  — the presentation number, its destination country
 *                           and the daily cap.
 *   2. Additional Settings— reputation score and the disabled/reason pair.
 *
 * Both panels stay MOUNTED (the inactive one is `hidden`) so react-hook-form
 * keeps every field registered and an error on the background tab still exists
 * in the DOM; the tab strip flags it with a dot.
 *
 * Validation mirrors the server (`routes/admin/number-pool/types.ts`) so the
 * operator gets immediate feedback, but the SERVER is the authority — this is
 * convenience, not the control. A malformed presentation number is what makes a
 * call unattestable under STIR/SHAKEN and invalid under Ofcom/ACMA, so the
 * boundary check lives on the API and is duplicated here only for UX.
 *
 * Backend: apps/api/src/routes/admin/number-pool/
 *   POST  /admin/number-pool      -> 201 + row
 *   PATCH /admin/number-pool/:id  -> 200 + row
 */
import { useRef, useState, type JSX } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

import ResourceDrawer from '../ResourceDrawer.js'
import {
  SectionCard,
  TabStrip,
  SwitchRow,
  useDrawerTabs,
} from '../DrawerFormKit.js'
import { ZodForm, FormText, FormNumber } from '../../forms/index.js'
import { FormGrid } from '../../forms/layout/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

export interface PoolNumber {
  id: string
  phoneNumber: string
  countryCode: string
  campaignId: string | null
  sipTrunkId: string | null
  reputationScore: number
  dailyCallCount: number
  dailyCap: number
  disabled: boolean
  disabledReason: string | null
  createdAt: string
}

/** Same expressions the API enforces (routes/admin/number-pool/types.ts). */
const E164 = /^\+[1-9]\d{1,14}$/
const COUNTRY = /^[A-Za-z]{2,3}$/

export interface NumberPoolDrawerProps {
  open: boolean
  value: PoolNumber | null
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const NumberPoolSchema = z
  .object({
    phoneNumber: z
      .string()
      .trim()
      .min(1, 'Phone number is required')
      .regex(E164, 'Must be E.164: a leading “+”, then 2–15 digits, no spaces or punctuation'),
    countryCode: z
      .string()
      .trim()
      .min(1, 'Destination country is required')
      .regex(COUNTRY, 'Must be an ISO alpha-2/3 code, e.g. US'),
    dailyCap: z
      .number({ message: 'Daily cap is required' })
      .int('Must be a whole number')
      .min(0, 'Must be ≥ 0')
      .max(100000, 'Must be ≤ 100000'),
    reputationScore: z
      .number({ message: 'Reputation score is required' })
      .int('Must be a whole number')
      .min(0, 'Must be ≥ 0')
      .max(100, 'Must be ≤ 100'),
    disabled: z.boolean(),
    disabledReason: z.string().trim().max(128, 'Max 128 characters'),
  })
  // A disabled number with no reason is the state that later reads as an
  // unexplained gap in the pool, so require the note when the switch is on.
  .superRefine((data, ctx) => {
    if (data.disabled && data.disabledReason === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disabledReason'],
        message: 'Give a reason when disabling a number',
      })
    }
  })

interface FormValues {
  phoneNumber: string
  countryCode: string
  dailyCap: number
  reputationScore: number
  disabled: boolean
  disabledReason: string
}

const BASIC_FIELDS = ['phoneNumber', 'countryCode', 'dailyCap'] as const
const ADDITIONAL_FIELDS = ['reputationScore', 'disabled', 'disabledReason'] as const

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NumberPoolDrawer({
  open,
  value,
  onClose,
  onSaved,
}: NumberPoolDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  /** Remount key — ZodForm mount-initializes and does not re-sync. */
  const contentKey = value?.id ?? 'new'

  const defaults: FormValues = {
    phoneNumber: value?.phoneNumber ?? '',
    countryCode: value?.countryCode ?? '',
    dailyCap: value?.dailyCap ?? 500,
    reputationScore: value?.reputationScore ?? 100,
    disabled: value?.disabled ?? false,
    disabledReason: value?.disabledReason ?? '',
  }

  async function handleSave(values: FormValues): Promise<void> {
    setSaving(true)
    try {
      const body = {
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode.toUpperCase(),
        reputationScore: values.reputationScore,
        dailyCap: values.dailyCap,
        disabled: values.disabled,
        disabledReason: values.disabled && values.disabledReason ? values.disabledReason : null,
      }
      if (value) {
        await api.patch(`/admin/number-pool/${value.id}`, body)
        toast.success('Number updated')
      } else {
        await api.post('/admin/number-pool', body)
        toast.success('Number added')
      }
      onSaved()
    } catch (err) {
      // 409 = already in the pool; surface it specifically rather than as a
      // generic failure, because it is the most common operator mistake.
      const status = (err as { status?: number }).status
      toast.error(status === 409 ? 'That number is already in the pool' : 'Failed to save number')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="xl"
      title={value ? `Edit Caller ID: ${value.phoneNumber}` : 'Add Caller ID'}
    >
      <ZodForm
        key={contentKey}
        schema={NumberPoolSchema}
        defaultValues={defaults}
        onSubmit={(data) => handleSave(data as FormValues)}
        formRef={formRef}
      >
        <NumberPoolTabs current={value} />
      </ZodForm>
    </ResourceDrawer>
  )
}

/**
 * The two-tab body. Lives inside `ZodForm` so it can read `formState` via
 * context — both for the per-tab error dots and to show the live usage figure
 * against whatever cap the operator is currently typing.
 */
function NumberPoolTabs({ current }: { current: PoolNumber | null }): JSX.Element {
  const { active, setActive, errorFor } = useDrawerTabs([BASIC_FIELDS, ADDITIONAL_FIELDS])
  const { watch } = useFormContext()
  const disabled = Boolean(watch('disabled'))

  return (
    <div className="kc3-form">
      <TabStrip
        idPrefix="number-pool"
        ariaLabel="Caller ID settings"
        activeIndex={active}
        onChange={setActive}
        tabs={[
          {
            key: 'basic',
            label: 'Basic Information',
            icon: 'pi pi-info-circle',
            error: errorFor(0),
          },
          {
            key: 'additional',
            label: 'Additional Settings',
            icon: 'pi pi-sliders-h',
            error: errorFor(1),
          },
        ]}
      />

      {/* Both panels stay mounted so RHF keeps every field registered. */}
      <div
        id="number-pool-panel-basic"
        role="tabpanel"
        aria-labelledby="number-pool-tab-basic"
        hidden={active !== 0}
        className="space-y-5"
      >
        <SectionCard
          title="Presentation Number"
          description="The number shown to the person being called, and where it may be used."
          icon="pi pi-phone"
        >
          <FormGrid columns={2}>
            <FormText
              name="phoneNumber"
              label="Phone Number (E.164)"
              required
              autoFocus
              placeholder="+14155550123"
              helpText="A leading “+”, then 2–15 digits. No spaces or punctuation."
            />
            <FormText
              name="countryCode"
              label="Destination Country (ISO code)"
              required
              placeholder="US"
              helpText="The dialer only picks a number whose country matches the number being called."
            />
          </FormGrid>
        </SectionCard>

        <SectionCard
          title="Daily Usage"
          description="How often this number may be presented before the dialer rests it."
          icon="pi pi-chart-bar"
        >
          <FormGrid columns={2}>
            <FormNumber
              name="dailyCap"
              label="Daily Cap"
              min={0}
              max={100000}
              required
              useGrouping={false}
              helpText="Maximum dials per day. Once reached the number is skipped until the daily reset."
            />
            {current && (
              <div className="mb-4 flex flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-700">Used today</span>
                <div className="flex h-10 items-center rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-800">
                  {current.dailyCallCount} of {current.dailyCap}
                </div>
                <small className="text-xs text-gray-500">
                  A number silently at its cap is skipped by the picker, which otherwise looks
                  like &ldquo;no caller ID&rdquo;.
                </small>
              </div>
            )}
          </FormGrid>
        </SectionCard>
      </div>

      <div
        id="number-pool-panel-additional"
        role="tabpanel"
        aria-labelledby="number-pool-tab-additional"
        hidden={active !== 1}
        className="space-y-5"
      >
        <SectionCard
          title="Selection Priority"
          description="How this number ranks against the rest of the pool."
          icon="pi pi-sort-amount-down"
        >
          <FormGrid columns={2}>
            <FormNumber
              name="reputationScore"
              label="Reputation Score"
              min={0}
              max={100}
              required
              useGrouping={false}
              helpText="0–100. Higher scores are preferred by the picker."
            />
          </FormGrid>
        </SectionCard>

        <SectionCard
          title="Availability"
          description="Take this number out of rotation without removing it from the pool."
          icon="pi pi-ban"
          bodyClassName="px-5"
        >
          <SwitchRow
            name="disabled"
            label="Disabled"
            description="A disabled number is never picked for an outbound call. Use this instead of deleting when a carrier flags the number — the history and reputation score are kept."
            onLabel="Disabled"
            offLabel="In rotation"
          />
          {disabled && (
            <div className="pb-1">
              <FormText
                name="disabledReason"
                label="Reason"
                required
                placeholder="Flagged as spam by carrier"
                helpText="Recorded so the next operator knows why this number is out (max 128 characters)."
              />
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  )
}
